<script setup lang="ts">
import { computed } from "vue"
import MediaBatchEditContent from "../media/batch/MediaBatchEditContent.vue"
import { useUploadEditSessionContext } from "./UploadEditSessionKey"
import { fromMediaFormData } from "./uploadToBatchEditItem"
import type { BatchResult } from "../media/batch/useBatchEditSession"
import type { MediaUpdateInput } from "../graphql/types"

const emit = defineEmits<{
  "cancel": []
  "applied": [result: BatchResult]
}>()

const session = useUploadEditSessionContext()

const items = computed(() => {
  const ids = Array.from(session.selectedIds.value)
  return ids
    .map(id => session.formDrafts.value.get(id))
    .filter((d): d is NonNullable<typeof d> => !!d)
    .map(fromMediaFormData)
})

async function saveOne(id: string, update: MediaUpdateInput): Promise<void> {
  // useBatchEditSession already resolved every changed array field to uuid[]
  // (merging the item's existing relations with the batch selection). Persist
  // that directly — reconstructing full entity objects from the uuids would drop
  // any relation the freshly uploaded file did not already carry.
  await session.saveSingleUpdate(id, update)
}

function onApplied(result: BatchResult): void {
  emit("applied", result)
}
</script>

<template>
  <!--
    `useBatchEditSession` snapshots `items` at creation. When the user deselects
    an item during batch mode, `selectedIds` shrinks, `items` computed shrinks,
    and we need to rebuild the session so the inner items list matches.
    `:key` forces a remount on set changes. This discards the in-progress
    patch state — an acceptable v1 tradeoff; a future improvement would be
    reactive sync inside the session.
  -->
  <MediaBatchEditContent
    :key="items.map(i => i.uuid).join(',')"
    :items="items"
    :save="saveOne"
    @applied="onApplied"
    @close="emit('cancel')"
  />
</template>
